import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { OPEN_ACCOUNT_EVENT } from '../app-open.js';
import { useConfirm } from './ConfirmDialog.jsx';

// A tank's Ring camera (private beta, server/routes/ring.js): the owner
// picks which of their Ring cameras shows the tank, and watches it live.
// Ring ends a live view after 60 seconds (30 on a battery camera) and puts
// its logo and the time on the picture. TankSensei keeps no video.

// GET /tanks/:id/ring for the owner: { available, camera }. Null while
// loading or when there is nothing to show (not in the beta, not the owner).
export function useTankRing(tank) {
  const isOwner = !tank.role || tank.role === 'owner';
  const [ring, setRing] = useState(null);
  const load = async () => {
    if (!isOwner) return setRing(null);
    try {
      setRing(await api(`/api/tanks/${tank.id}/ring`));
    } catch {
      setRing(null);
    }
  };
  useEffect(() => {
    load();
  }, [tank.id, isOwner]);
  return [ring, load];
}

export function ringRowText(ring) {
  if (!ring?.camera) return 'Watch this tank live from a Ring camera';
  if (ring.camera.needs_relink) return 'Ring needs you to link again';
  return 'Tap to watch live';
}

const LIVE_SECONDS = 60;
const ICE_WAIT_MS = 6000;
// A phone network that blocks the video never says so: the connection just
// stays at "connecting", so it is given up on after this long.
const CONNECT_TIMEOUT_MS = 20000;
const DROP_GRACE_MS = 2000;
const NO_ROUTE_WORDS =
  'Could not get the video through this network. Try again on Wi-Fi, or watch in the Ring app.';

function waitForIce(pc) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      if (pc.iceGatheringState === 'complete') {
        pc.removeEventListener('icegatheringstatechange', done);
        resolve();
      }
    };
    pc.addEventListener('icegatheringstatechange', done);
    setTimeout(resolve, ICE_WAIT_MS);
  });
}

function LiveView({ tank, name }) {
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const sessionRef = useRef(null);
  const [state, setState] = useState('idle'); // idle | starting | live | ended
  const [left, setLeft] = useState(LIVE_SECONDS);
  const [err, setErr] = useState('');
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const stop = async (to = 'ended') => {
    clearTimers();
    const pc = pcRef.current;
    pcRef.current = null;
    try {
      pc?.close();
    } catch {
      // already closed
    }
    const sid = sessionRef.current;
    sessionRef.current = null;
    if (sid) {
      api(`/api/tanks/${tank.id}/ring/live/${sid}`, { method: 'DELETE' }).catch(() => {});
    }
    setState(to);
  };

  useEffect(() => () => void stop('idle'), []);

  useEffect(() => {
    if (state !== 'live') return undefined;
    setLeft(LIVE_SECONDS);
    const t = setInterval(() => {
      setLeft((n) => {
        if (n <= 1) {
          clearInterval(t);
          stop();
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [state]);

  const start = async () => {
    if (pcRef.current) return;
    if (typeof RTCPeerConnection === 'undefined') {
      setErr('This browser cannot play live video. Try Safari or Chrome.');
      return;
    }
    setErr('');
    setState('starting');
    let pc = null;
    try {
      pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pcRef.current = pc;
      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });
      // One stream for both tracks: an answer without stream ids gives each
      // track alone, and the audio arriving second would replace the video.
      const stream = new MediaStream();
      pc.ontrack = (e) => {
        const v = videoRef.current;
        if (!v) return;
        stream.addTrack(e.track);
        if (v.srcObject !== stream) v.srcObject = stream;
        v.play?.().catch(() => {});
        if (e.track.kind === 'video') {
          e.track.addEventListener('ended', () => {
            if (pcRef.current === pc) stop();
          });
        }
      };
      let dropTimer = null;
      let wasLive = false;
      const noRoute = () => {
        stop('idle');
        setErr(NO_ROUTE_WORDS);
      };
      pc.onconnectionstatechange = () => {
        if (pcRef.current !== pc) return;
        const cs = pc.connectionState;
        if (cs === 'connected') {
          wasLive = true;
          clearTimeout(dropTimer);
          clearTimers();
          setState('live');
        } else if (cs === 'disconnected') {
          // Ring ending its session shows here first; a short blip recovers.
          dropTimer = setTimeout(() => {
            if (pcRef.current === pc && pc.connectionState === 'disconnected') stop();
          }, DROP_GRACE_MS);
          timersRef.current.push(dropTimer);
        } else if (cs === 'failed' || cs === 'closed') {
          if (wasLive) stop();
          else noRoute();
        }
      };
      await pc.setLocalDescription(await pc.createOffer());
      await waitForIce(pc);
      const res = await fetch(`/api/tanks/${tank.id}/ring/live`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/sdp' },
        body: pc.localDescription.sdp,
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error || 'Could not start the live view.');
      if (pcRef.current !== pc) {
        // Stopped while Ring was answering: end the session Ring just opened.
        if (body?.session) {
          api(`/api/tanks/${tank.id}/ring/live/${body.session}`, { method: 'DELETE' }).catch(
            () => {}
          );
        }
        return;
      }
      sessionRef.current = body.session;
      await pc.setRemoteDescription({ type: 'answer', sdp: body.answer });
      // Timed from Ring's answer: a slow Ring is not a network that blocks video.
      timersRef.current.push(
        setTimeout(() => {
          if (pcRef.current === pc && !wasLive) noRoute();
        }, CONNECT_TIMEOUT_MS)
      );
    } catch (e) {
      if (pcRef.current !== pc) return;
      await stop('idle');
      setErr(e.message || 'Could not start the live view.');
    }
  };

  return (
    <div className="ring-live">
      <div className="ring-video-box">
        <video
          ref={videoRef}
          className="ring-video"
          playsInline
          autoPlay
          muted
          aria-label={`Live view from ${name}`}
        />
        {state !== 'live' && (
          <div className="ring-video-cover">
            {state === 'starting'
              ? 'Connecting to the camera…'
              : state === 'ended'
                ? 'The live view has ended.'
                : name}
          </div>
        )}
      </div>
      {state === 'live' && (
        <p className="ts-hint" aria-live="polite">
          Live. Ring ends it in about {left} s (sooner on a battery camera).
        </p>
      )}
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
      {state === 'live' || state === 'starting' ? (
        <button
          type="button"
          className="ts-chip ring-big"
          onClick={() => stop(state === 'live' ? 'ended' : 'idle')}
        >
          Stop
        </button>
      ) : (
        <button type="button" className="ts-save ring-big" onClick={start}>
          {state === 'ended' ? 'Watch again' : 'Watch live'}
        </button>
      )}
    </div>
  );
}

export function RingCameraDetail({ tank, ring, onReload, onBack }) {
  const confirm = useConfirm();
  const [cams, setCams] = useState(null);
  const [picking, setPicking] = useState(!ring?.camera);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!picking) return;
    api('/api/ring/cameras')
      .then(setCams)
      .catch(() => setCams({ cameras: [], problems: [{ code: 'failed' }] }));
  }, [picking]);

  const pick = async (cam, component = null) => {
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/ring`, {
        method: 'PUT',
        body: { link_id: cam.link_id, device_id: cam.id, component_id: component?.id || null },
      });
      await onReload?.();
      setPicking(false);
    } catch (e) {
      setErr(e.message || 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Take ${ring.camera.name} off this tank?`,
      body: 'The camera stays linked to your account; it just no longer shows on this tank.',
      confirmLabel: 'Take it off',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api(`/api/tanks/${tank.id}/ring`, { method: 'DELETE' });
      await onReload?.();
      setPicking(true);
    } catch (e) {
      setErr(e.message || 'Could not do that just now.');
    } finally {
      setBusy(false);
    }
  };

  const openAccount = () =>
    window.dispatchEvent(new CustomEvent(OPEN_ACCOUNT_EVENT, { detail: 'ring' }));

  return (
    <section className="ts-section eq-screen">
      <div className="eq-screen-head">
        <button type="button" className="eq-back" onClick={onBack}>
          ← Equipment
        </button>
        <h3 className="font-heading" style={{ margin: 0 }}>
          {ring?.camera && !picking ? ring.camera.name : 'Ring camera'}
        </h3>
      </div>

      {ring?.camera && !picking ? (
        ring.camera.needs_relink ? (
          <p className="ts-hint ring-warn">
            Ring needs you to link again: in the Ring app, remove TankSensei, then add it again.
            Then pick the camera on each tank again.
          </p>
        ) : (
          <>
            <LiveView tank={tank} name={ring.camera.name} />
            <p className="ts-hint">
              Ring puts its logo and the time on the picture. TankSensei keeps no video; Ring may
              save it in your Ring history.
            </p>
            <div className="dvh-actions">
              <button
                type="button"
                className="ts-chip dvh-remove"
                onClick={() => setPicking(true)}
                disabled={busy}
              >
                Use another camera
              </button>
              <button
                type="button"
                className="ts-chip ts-chip-danger dvh-remove"
                onClick={remove}
                disabled={busy}
              >
                Take it off this tank
              </button>
            </div>
          </>
        )
      ) : cams === null ? (
        <p className="ts-hint">Loading your Ring cameras…</p>
      ) : cams.cameras.length ? (
        <>
          <p className="ts-hint">Which camera shows {tank.name || 'this tank'}?</p>
          <ul className="dvh-list" aria-label="Your Ring cameras">
            {cams.cameras.flatMap((c) =>
              (c.components.length ? c.components : [null]).map((comp) => (
                <li key={`${c.link_id}:${c.id}:${comp?.id || ''}`}>
                  <button
                    type="button"
                    className="dvh-row"
                    onClick={() => pick(c, comp)}
                    disabled={busy || c.encrypted}
                  >
                    <span className="dvh-row-text">
                      <span className="dvh-row-title">
                        {comp ? `${c.name}, ${comp.name}` : c.name}
                      </span>
                      <span
                        className={`dvh-row-sub${c.encrypted || c.online === false ? ' dvh-row-sub--quiet' : ''}`}
                      >
                        {c.encrypted
                          ? 'End-to-end encrypted in the Ring app, so no other app can show it'
                          : c.online === false
                            ? 'Offline'
                            : 'Use this camera'}
                      </span>
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
          {ring?.camera && (
            <button type="button" className="ts-chip ring-tap" onClick={() => setPicking(false)}>
              Keep {ring.camera.name}
            </button>
          )}
        </>
      ) : (
        <>
          <p className="ts-hint">
            {cams.problems?.some((p) => p.code === 'needs_relink')
              ? 'Ring needs you to link again: in the Ring app, remove TankSensei, then add it again. Then pick the camera on each tank again.'
              : cams.problems?.length
                ? 'Could not reach Ring just now. Try again in a minute.'
                : 'No Ring cameras linked yet. Link Ring in Account, then come back here.'}
          </p>
          <button type="button" className="ts-chip ring-tap" onClick={openAccount}>
            Ring in Account
          </button>
        </>
      )}
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
    </section>
  );
}
